import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  API_CONFIG,
  API_ENDPOINTS,
  STORAGE_KEYS,
} from "../helpers/constants";
import type { DownloadSourceParams } from "../helpers/dataSourceUtils";
import { DATA_SOURCES_DIALOG_Z_INDEX } from "../helpers/zIndexLayers";

export interface DatasourceViewModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Called when the modal should close */
  onClose: () => void;
  /** Datasource id for the download API */
  datasourceId: string;
  /** Display name for the datasource (e.g. file name) */
  displayName: string;
  /** When set, passed as gcp/azure/s3 query params to the download API (one true for that cloud, else all false) */
  downloadSource?: DownloadSourceParams | null;
}

/**
 * Builds query string for the download API. Always passes view.
 * gcp/azure/s3 are only included when true (omit when false).
 */
function buildDownloadQuery(
  view: boolean,
  source?: DownloadSourceParams | null
): string {
  const params = new URLSearchParams({
    view: String(view),
  });
  // Only add gcp/azure/s3 when true; do not pass them when false
  if (source?.gcp) params.set("gcp", "true");
  if (source?.azure) params.set("azure", "true");
  if (source?.s3) params.set("s3", "true");
  return `?${params.toString()}`;
}

/** Result of download API: either file blob or redirect URL (e.g. Google Drive). */
type DatasourceDownloadResult =
  | { type: "blob"; blob: Blob }
  | { type: "url"; url: string };

/**
 * Fetches datasource file content. GET with view=true for in-browser display, view=false for download.
 * Returns blob for normal files, or { url } when API returns JSON (e.g. Google Drive view/download URL).
 * Query params: view, gcp, azure, s3 (only one of gcp/azure/s3 true when from that cloud).
 */
async function fetchDatasource(
  datasourceId: string,
  view: boolean,
  downloadSource?: DownloadSourceParams | null
): Promise<DatasourceDownloadResult> {
  const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
  if (!token) {
    throw new Error("No refresh token found");
  }
  const query = buildDownloadQuery(view, downloadSource);
  const url = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.DOWNLOAD(datasourceId)}${query}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json,application/pdf,image/*,*/*",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to fetch datasource: ${response.status}`
    );
  }
  const contentType = response.headers.get("content-type") || "";
  // When API returns JSON with url (e.g. Google Drive), use that URL for view/download
  if (contentType.includes("application/json")) {
    const data = await response.json();
    const redirectUrl = data?.url;
    if (typeof redirectUrl === "string" && redirectUrl) {
      return { type: "url", url: redirectUrl };
    }
  }
  const blob = await response.blob();
  return { type: "blob", blob };
}

/**
 * Downloads a datasource file by fetching with view=false. For blob response, triggers browser download;
 * for JSON { url } (e.g. Google Drive), opens the URL in a new tab so user can view/download from Drive.
 * Pass downloadSource when the datasource is from GCP/Azure/S3 so the API uses the correct vault source.
 */
export async function downloadDatasource(
  datasourceId: string,
  fileName?: string,
  downloadSource?: DownloadSourceParams | null
): Promise<void> {
  const result = await fetchDatasource(datasourceId, false, downloadSource);
  if (result.type === "url") {
    window.open(result.url, "_blank", "noopener,noreferrer");
    return;
  }
  const objectUrl = URL.createObjectURL(result.blob);
  const downloadFileName = fileName || `datasource-${datasourceId}`;
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = downloadFileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
}

/**
 * Modal that fetches and displays a datasource file (e.g. PDF, images) via the
 * datasource download API with view=true for in-browser display.
 */
/** Infer MIME type from file extension when blob type is absent or generic. */
function inferMimeFromName(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  const map: Record<string, string> = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    svg: "image/svg+xml",
    txt: "text/plain",
    csv: "text/csv",
    html: "text/html",
  };
  return map[ext] ?? "application/octet-stream";
}

/**
 * Allowlist of MIME types this modal will render inline (via <iframe> or <object>).
 *
 * PR #607 review: the datasource upload route stores `file_type` straight from the
 * uploading client's declared Content-Type, with no server-side allowlist — so `mimeType`
 * here is attacker-controllable. The CSP relaxation that made inline preview work at all
 * (object-src/frame-src now permit blob:) means a file uploaded with Content-Type
 * "text/html" or "image/svg+xml" containing a <script> would otherwise execute as
 * same-origin content the moment anyone previews it. Deliberately NOT denylisting just
 * text/html and image/svg+xml — allowlisting only the types genuinely safe to render
 * (PDF, and raster image formats that cannot carry executable script content) means any
 * other claimed type, known-dangerous or not yet thought of, falls through to
 * download-only instead of an inline-render attempt.
 */
const PREVIEWABLE_MIME_TYPES = new Set([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);

const DatasourceViewModal: React.FC<DatasourceViewModalProps> = ({
  open,
  onClose,
  datasourceId,
  displayName,
  downloadSource,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string>("application/octet-stream");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !datasourceId) {
      setBlobUrl(null);
      setError(null);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    setLoading(true);
    setError(null);
    setBlobUrl(null);

    (async () => {
      try {
        const result = await fetchDatasource(datasourceId, true, downloadSource);
        if (cancelled) return;

        let blob: Blob;

        if (result.type === "url") {
          // External redirect URL (e.g. presigned S3/GCP/Azure or Google Drive).
          // Putting it directly in an iframe fails because those servers set
          // X-Frame-Options: DENY. Fetch the content and create a local blob URL instead.
          try {
            const res = await fetch(result.url, {
              mode: "cors",
              credentials: "omit",
            });
            if (!res.ok) throw new Error("fetch failed");
            blob = await res.blob();
          } catch {
            // CORS or auth failure (e.g. Google Drive) — fall back to direct URL.
            // The caller-side fallback text inside <object> will show if it still blocks.
            if (!cancelled) {
              setMimeType(inferMimeFromName(displayName));
              setBlobUrl(result.url);
              setLoading(false);
            }
            return;
          }
        } else {
          blob = result.blob;
        }

        if (cancelled) return;

        // Resolve MIME type: prefer the blob's own type; fall back to filename inference.
        const resolvedType =
          blob.type && blob.type !== "application/octet-stream"
            ? blob.type
            : inferMimeFromName(displayName);

        // Re-wrap blob only when the type needs correcting (avoids unnecessary copy).
        const finalBlob =
          blob.type === resolvedType
            ? blob
            : new Blob([await blob.arrayBuffer()], { type: resolvedType });

        if (cancelled) return;

        objectUrl = URL.createObjectURL(finalBlob);
        setMimeType(resolvedType);
        setBlobUrl(objectUrl);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load datasource"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, datasourceId, downloadSource]);

  // Normalize before comparing: a backend/CDN Content-Type can carry a parameter (e.g.
  // "application/pdf; charset=binary" — nonstandard for PDF, but seen from misconfigured
  // S3/proxy setups for binary types) which would otherwise fall through to the <object>
  // branch and reintroduce the exact rendering bug this component fixes (PR #606 review).
  const normalizedMimeType = mimeType.split(";")[0].trim();
  const isPdfMimeType = normalizedMimeType === "application/pdf";
  // PR #607 review: mimeType is attacker-controllable (upload-time Content-Type has no
  // server-side allowlist), so only attempt an inline render for types on the known-safe
  // allowlist above — everything else (text/html, image/svg+xml, anything unrecognized)
  // goes straight to download-only, never through <iframe>/<object> at all.
  const isPreviewable = PREVIEWABLE_MIME_TYPES.has(normalizedMimeType);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      // Above DataSourcesPanel's own zIndex (1350), which this modal is opened from
      sx={{ zIndex: DATA_SOURCES_DIALOG_Z_INDEX }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography component="span" variant="h6" sx={{ fontWeight: "bold" }}>
          {displayName}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ color: "text.secondary" }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0, minHeight: 400 }}>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && blobUrl && isPdfMimeType && (
          <Box>
            {/* Persistent download link, always visible alongside the iframe — not inside
                it. <iframe>, unlike <object>, has no fallback-content mechanism at all: if a
                locked-down browser build has its PDF viewer disabled or the blob fails to
                render, the iframe just shows blank with zero way to recover. This keeps an
                escape hatch available regardless of whether the iframe itself renders
                (PR #606 review). */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Typography variant="body2">
                <a href={blobUrl} download={displayName}>
                  Download file
                </a>
              </Typography>
            </Box>
            {/* <iframe> is used for PDFs specifically: browser support for
                <object type="application/pdf"> is inconsistent across Chromium builds and
                enterprise/embedded configurations, silently falling back to the <object>'s
                child content ("Unable to display this file inline") even for a perfectly
                valid PDF. <iframe src="blob:..."> reliably invokes the browser's native PDF
                viewer across Chrome, Edge, Firefox, and Safari. */}
            <iframe
              src={blobUrl}
              title={displayName}
              style={{
                width: "100%",
                minHeight: 500,
                border: "none",
                borderRadius: 4,
                display: "block",
              }}
            />
          </Box>
        )}
        {!loading && !error && blobUrl && !isPdfMimeType && isPreviewable && (
          <object
            data={blobUrl}
            type={mimeType}
            title={displayName}
            style={{
              width: "100%",
              minHeight: 500,
              border: "none",
              borderRadius: 4,
              display: "block",
            }}
          >
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Unable to display this file inline.{" "}
                <a href={blobUrl} download={displayName}>
                  Click here to download
                </a>
                .
              </Typography>
            </Box>
          </object>
        )}
        {/* Not on the safe-preview allowlist (PR #607 review) — e.g. a file uploaded with a
            claimed Content-Type of text/html or image/svg+xml, both capable of carrying
            executable script. Never attempted via <iframe>/<object> at all; download-only,
            same as the pre-existing fallback message shown when a previewable type's inline
            render itself fails. */}
        {!loading && !error && blobUrl && !isPreviewable && (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              This file type can't be previewed here.{" "}
              <a href={blobUrl} download={displayName}>
                Click here to download
              </a>
              .
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DatasourceViewModal;
